import { useEffect, useState, type ReactNode } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import AdminPageShell from "../../components/admin/AdminPageShell";
import Badge from "../../components/ui/badge/Badge";
import {
  adminAlertErrorClass,
  adminPrimaryButtonClass,
  adminSecondaryButtonClass,
  adminSpinnerClass,
} from "../../components/admin/adminStyles";

interface Driver {
  id: string;
  fullName: string;
  nationalInsuranceNumber?: string;
  vatNumber?: string;
  address?: string;
  postCode?: string;
  licensingAuthority?: string;
  mobileNumber: string;
  emergencyContact?: string;
  communicationEmail: string;
  bookingEmail?: string;
  adminUser?: string;
  accountName?: string;
  accountNumber?: string;
  sortCode?: string;
  bankName?: string;
  bankAddress?: string;
  workFrom?: string | null;
  workUpTo?: string | null;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
  isAvailable: boolean;
  subcompanyId: string | null;
  subcompany?: { id: string; name: string } | null;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function DetailField({
  label,
  value,
  className = "",
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1.5 text-sm font-medium text-gray-900 dark:text-white/90">{value}</dd>
    </div>
  );
}

function SectionCard({
  title,
  children,
  className = "",
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6 ${className}`}
    >
      <h3 className="mb-5 text-base font-semibold text-gray-900 dark:text-white/90">{title}</h3>
      {children}
    </section>
  );
}

function DayTag({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
        active
          ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200 dark:bg-brand-500/15 dark:text-brand-300 dark:ring-brand-500/30"
          : "bg-gray-100 text-gray-500 ring-1 ring-gray-200 dark:bg-white/5 dark:text-gray-400 dark:ring-gray-700"
      }`}
    >
      {label}
    </span>
  );
}

const WEEKDAYS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
] as const;

export default function DriverDetail() {
  const { id } = useParams();
  const [driver, setDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDriverData();
  }, [id]);

  const fetchDriverData = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get(`/drivers/${id}`);
      setDriver(response.data);
    } catch (fetchError) {
      setError("Failed to fetch driver data");
      console.error("Error fetching driver:", fetchError);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminPageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className={adminSpinnerClass} />
        </div>
      </AdminPageShell>
    );
  }

  if (error) {
    return (
      <AdminPageShell>
        <div className={adminAlertErrorClass}>{error}</div>
      </AdminPageShell>
    );
  }

  if (!driver) {
    return (
      <AdminPageShell>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">Driver not found</p>
      </AdminPageShell>
    );
  }

  const workHours =
    driver.workFrom || driver.workUpTo
      ? `${driver.workFrom || "—"} – ${driver.workUpTo || "—"}`
      : "—";

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle="Driver Details"
        description="Profile, contact, banking, and schedule"
      />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white/90">
              {driver.fullName}
            </h2>
            <Badge color={driver.isAvailable ? "success" : "light"} size="sm">
              {driver.isAvailable ? "Available" : "Unavailable"}
            </Badge>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {driver.subcompany?.name ?? "Parent company (direct)"}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/drivers" className={adminSecondaryButtonClass}>
            Back to list
          </Link>
          <Link to={`/drivers/${driver.id}`} className={adminPrimaryButtonClass}>
            Edit driver
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SectionCard title="Personal information">
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <DetailField label="Full name" value={displayValue(driver.fullName)} />
            <DetailField
              label="National Insurance"
              value={displayValue(driver.nationalInsuranceNumber)}
            />
            <DetailField label="VAT number" value={displayValue(driver.vatNumber)} />
            <DetailField
              label="Licensing authority"
              value={displayValue(driver.licensingAuthority)}
            />
            <DetailField
              label="Address"
              value={displayValue(driver.address)}
              className="sm:col-span-2"
            />
            <DetailField label="Post code" value={displayValue(driver.postCode)} />
          </dl>
        </SectionCard>

        <SectionCard title="Contact">
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <DetailField label="Mobile" value={displayValue(driver.mobileNumber)} />
            <DetailField
              label="Emergency contact"
              value={displayValue(driver.emergencyContact)}
            />
            <DetailField
              label="Communication email"
              value={displayValue(driver.communicationEmail)}
              className="sm:col-span-2"
            />
            <DetailField
              label="Booking email"
              value={displayValue(driver.bookingEmail)}
              className="sm:col-span-2"
            />
          </dl>
        </SectionCard>

        <SectionCard title="Portal access">
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <DetailField label="Admin username" value={displayValue(driver.adminUser)} />
          </dl>
        </SectionCard>

        <SectionCard title="Banking">
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <DetailField label="Account name" value={displayValue(driver.accountName)} />
            <DetailField label="Account number" value={displayValue(driver.accountNumber)} />
            <DetailField label="Sort code" value={displayValue(driver.sortCode)} />
            <DetailField label="Bank name" value={displayValue(driver.bankName)} />
            <DetailField
              label="Bank address"
              value={displayValue(driver.bankAddress)}
              className="sm:col-span-2"
            />
          </dl>
        </SectionCard>

        <SectionCard title="Work schedule" className="xl:col-span-2">
          <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <DetailField
              label="Company"
              value={driver.subcompany?.name ?? "Parent company (direct)"}
            />
            <DetailField label="Work hours" value={workHours} />
            <DetailField
              label="Availability"
              value={driver.isAvailable ? "Available for bookings" : "Not available"}
            />
          </dl>
          <div className="mt-5">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Working days
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {WEEKDAYS.map(({ key, label }) => (
                <DayTag key={key} active={Boolean(driver[key])} label={label} />
              ))}
            </div>
          </div>
        </SectionCard>
      </div>
    </AdminPageShell>
  );
}
